import { Fragment } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { DEFAULT_VALUES } from "@/libraries/web-framework";

import {
    FormattedBreakdownPriceTier,
    FormattedPriceCalculationResponse
} from "../constants";
import { usePriceFinderStore } from "../store/store";
import { getFormattedPrice } from "../utils/amounts-format";

export const PricingExample = () => {
    const [
        {
            billingCycle,
            exampleMonthlyUnitCount,
            exampleAnnualUnitCount,
            messages
        }
    ] = usePriceFinderStore();

    return (
        <div css={css({ maxWidth: "568px", margin: "auto" })}>
            <div
                css={css({
                    "marginBlockEnd": "24px",
                    "lineHeight": "1",
                    "@media (min-width: 1024px)": {
                        textAlign: "center",
                        lineHeight: "24px",
                        margin: "5px 50px 24px"
                    }
                })}
            >
                {billingCycle === "MONTHLY" && (
                    <FormattedMessage {...messages.monthlyExampleHelpText} />
                )}
                {billingCycle === "ANNUAL" && (
                    <FormattedMessage {...messages.annualExampleHelpText} />
                )}
            </div>
            <p
                css={css({
                    fontSize: ".75rem",
                    fontStyle: "italic",
                    lineHeight: "18px",
                    margin: 0
                })}
            >
                {billingCycle === "MONTHLY" && (
                    <FormattedMessage
                        id="Pricing.priceFinder.example.monthlyBillExample"
                        defaultMessage="Monthly billing example"
                    />
                )}
                {billingCycle === "ANNUAL" && (
                    <FormattedMessage
                        id="Pricing.priceFinder.example.annualBillExample"
                        defaultMessage="Annual billing example"
                    />
                )}
            </p>
            <div
                css={css({
                    "@media (min-width: 1024px)": {
                        backgroundColor: "white",
                        border: "1px solid rgb(151, 160, 175)",
                        borderRadius: "4px",
                        marginBlockStart: "4px",
                        padding: "28px 48px 24px 32px"
                    }
                })}
            >
                <h3
                    css={css({
                        fontSize: "1.25rem",
                        fontWeight: 500,
                        lineHeight: "24px",
                        margin: "4px 0"
                    })}
                >
                    <FormattedMessage
                        id="Pricing.priceFinder.example.billingDetailsBreakdown"
                        defaultMessage="Billing details breakdown"
                    />
                </h3>
                <div
                    css={css({
                        fontSize: ".88rem"
                    })}
                >
                    <FormattedMessage
                        {...messages.exampleStandardPlan}
                        values={{
                            ...DEFAULT_VALUES,
                            unitCount:
                                billingCycle === "MONTHLY"
                                    ? exampleMonthlyUnitCount
                                    : exampleAnnualUnitCount
                        }}
                    />
                </div>
                <div css={css({ minHeight: "240px" })}>
                    {billingCycle === "MONTHLY" && <MonthlyBreakdown />}
                    {billingCycle === "ANNUAL" && <AnnualBreakdown />}
                </div>
            </div>
        </div>
    );
};

const positionRelative = css({
    position: "relative"
});

const breakdownFontSize = css({
    "fontSize": ".88rem",
    "@media (min-width: 1024px)": {
        fontSize: "1rem"
    }
});

const MonthlyBreakdown = () => {
    const [
        { priceCalcs, exampleMonthlyUnitCount, messages, showFirstFlatRateTier }
    ] = usePriceFinderStore();

    const {
        hasFlatRateFirstTier,
        formattedTotalPrice,
        formattedAvgPerUnitPrice,
        formattedBreakdown
    } = priceCalcs[priceCalcs.length - 1] as FormattedPriceCalculationResponse;

    return (
        <div css={breakdownFontSize}>
            <div css={positionRelative}>
                <div
                    css={css({
                        margin: "16px 0 4px",
                        fontWeight: 700
                    })}
                >
                    <strong>
                        <FormattedMessage
                            {...messages.monthlyExampleUnitCountText}
                        />
                    </strong>
                </div>
                <div
                    css={css({
                        "display": "grid",
                        "gridTemplateColumns": "repeat(3, max-content)",
                        "gap": "0 6px",
                        "lineHeight": "18px",
                        "@media (min-width: 1024px)": {
                            lineHeight: "24px"
                        }
                    })}
                >
                    {formattedBreakdown.map(
                        (tier: FormattedBreakdownPriceTier, index: number) => {
                            const isFlatRateTier =
                                showFirstFlatRateTier &&
                                hasFlatRateFirstTier &&
                                index === 0;

                            return (
                                <Fragment key={index}>
                                    <span css={css({ textAlign: "right" })}>
                                        {tier.formattedUnitsInTier}{" "}
                                    </span>
                                    <span>
                                        <FormattedMessage
                                            {...messages.monthlyExampleUnitCountAt}
                                        />{" "}
                                    </span>
                                    <span css={css({ textAlign: "right" })}>
                                        <strong>
                                            {isFlatRateTier
                                                ? tier.formattedTotalPriceForTier
                                                : tier.formattedAvgPerUnitPriceForTier}
                                            {isFlatRateTier && (
                                                <Fragment>
                                                    {" "}
                                                    <FormattedMessage
                                                        id="Pricing.priceFinder.example.flatRate"
                                                        defaultMessage="flat rate"
                                                        description="a pricing rate where the price does not change based on the number of users"
                                                    />
                                                </Fragment>
                                            )}
                                        </strong>
                                    </span>
                                </Fragment>
                            );
                        }
                    )}
                </div>
                <div // divider
                    css={css({
                        "borderBlockStart": "1px solid #091e42",
                        "marginBlockStart": "8px",
                        "width": "140px",
                        "@media (min-width: 1024px)": {
                            width: "180px"
                        }
                    })}
                />
                <div
                    css={css({
                        marginBlockStart: "8px"
                    })}
                >
                    <strong>
                        <FormattedMessage
                            {...messages.monthlyExampleUnitCountTotal}
                            values={{
                                ...DEFAULT_VALUES,
                                unitCount: exampleMonthlyUnitCount
                            }}
                        />
                    </strong>
                </div>
                <DiamondSVG />
            </div>
            <div
                css={css({
                    margin: "12px 0 24px"
                })}
            >
                <div>
                    <FormattedMessage
                        id="Pricing.priceFinder.example.monthlyCost"
                        defaultMessage={
                            "Monthly cost: <strong>{price}</strong>"
                        }
                        values={{
                            ...DEFAULT_VALUES,
                            price: formattedTotalPrice
                        }}
                    />
                </div>
                <div>
                    <FormattedMessage
                        {...messages.monthlyExampleEstimatedPrice}
                        values={{
                            ...DEFAULT_VALUES,
                            price: formattedAvgPerUnitPrice
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const AnnualBreakdown = () => {
    const [{ priceCalcs, exampleAnnualUnitCount, currencyISOCode, messages }] =
        usePriceFinderStore();

    const { formattedTotalPrice, formattedBreakdown, breakdown, totalPrice } =
        priceCalcs[priceCalcs.length - 1] as FormattedPriceCalculationResponse;

    const existingFmtBreakdown = formattedBreakdown[0];
    const totalUnits = breakdown.reduce(
        // linting is broken in pipeline (for no reason) where it thinks these are untyped
        // and freaks out about trying to use plus operand on untyped vars
        (totalUnits: number, { unitsInTier }) =>
            totalUnits + Number(unitsInTier),
        0
    );

    if (!existingFmtBreakdown) {
        throw new Error("Formatted pricing breakdown was undefined.");
    }

    const {
        formattedUnitsAvailableInTier,
        formattedUnitLimitLower,
        formattedUnitLimitUpper
    } = existingFmtBreakdown;

    return (
        <div css={breakdownFontSize}>
            <div
                css={[
                    positionRelative,
                    css({
                        "display": "grid",
                        "gap": "8px",
                        "lineHeight": "18px",
                        "margin": "16px 0 4px",
                        "@media (min-width: 1024px)": {
                            lineHeight: "24px",
                            gridTemplateColumns: "1fr 2fr"
                        }
                    })
                ]}
            >
                <div
                    css={css({
                        "@media (min-width: 1024px)": {
                            gridColumn: "1"
                        }
                    })}
                >
                    <strong>
                        <FormattedMessage
                            {...messages.annualExampleUnitCountTierText}
                        />
                    </strong>
                    <div>
                        <FormattedMessage
                            id="Pricing.priceFinder.example.unitCountRange"
                            defaultMessage="{minRange} - {maxRange}"
                            values={{
                                ...DEFAULT_VALUES,
                                minRange: formattedUnitLimitLower,
                                maxRange: formattedUnitLimitUpper
                            }}
                        />
                    </div>
                </div>
                <div
                    css={css({
                        "@media (min-width: 1024px)": {
                            gridColumn: "2"
                        }
                    })}
                >
                    <strong>
                        <FormattedMessage
                            id="Pricing.priceFinder.example.filledSeats"
                            defaultMessage="Filled seats"
                        />
                    </strong>
                    <div>{exampleAnnualUnitCount}</div>
                </div>
                <div
                    css={css({
                        "@media (min-width: 1024px)": {
                            gridColumn: "2"
                        }
                    })}
                >
                    <strong>
                        <FormattedMessage
                            id="Pricing.priceFinder.example.openSeats"
                            defaultMessage="Open seats"
                        />
                    </strong>
                    <div>{formattedUnitsAvailableInTier}</div>
                </div>
                <DiamondSVG />
            </div>
            <div
                css={css({
                    "margin": "12px 0 24px",
                    "@media (min-width: 1024px)": {
                        margin: "48px 0 24px"
                    }
                })}
            >
                <div>
                    <FormattedMessage
                        id="Pricing.priceFinder.example.annualCost"
                        defaultMessage="Annual cost: <strong>{price}</strong>"
                        values={{
                            ...DEFAULT_VALUES,
                            price: formattedTotalPrice
                        }}
                    />
                </div>
                <div>
                    <FormattedMessage
                        {...messages.annualExampleEstimatedPrice}
                        values={{
                            ...DEFAULT_VALUES,
                            price: getFormattedPrice(
                                currencyISOCode,
                                totalPrice / totalUnits / 12
                            )
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const DiamondSVG = () => {
    return (
        <div
            css={css({
                "position": "absolute",
                "insetInlineEnd": "16px",
                "insetBlockStart": "15%",
                "svg": {
                    width: "68px",
                    height: "68px"
                },
                "@media (min-width: 1024px)": {
                    insetInlineEnd: 0,
                    insetBlockStart: 0,
                    svg: {
                        width: "160px",
                        height: "160px"
                    }
                }
            })}
        >
            <svg
                width="160"
                height="160"
                viewBox="0 0 160 160"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g clipPath="url(#clip0_237_1573)">
                    <rect
                        width="160"
                        height="160"
                        fill="white"
                        fillOpacity="0.01"
                    />
                    <path
                        d="M129.953 89.7698L112.77 107.044C112.432 107.394 112.167 107.808 111.99 108.261C111.812 108.715 111.727 109.2 111.737 109.687V157.168C111.737 157.919 112.034 158.639 112.562 159.17C113.09 159.701 113.807 160 114.554 160H150.61C151.357 160 152.074 159.701 152.602 159.17C153.13 158.639 153.427 157.919 153.427 157.168V109.687C153.443 109.191 153.35 108.697 153.156 108.24C152.961 107.783 152.669 107.375 152.3 107.044L135.211 89.7698C134.509 89.0779 133.565 88.6904 132.582 88.6904C131.599 88.6904 130.655 89.0779 129.953 89.7698Z"
                        fill="#FFAB00"
                    />
                    <path
                        d="M81.3145 129.888L2.62909 50.7847H55.0235L81.3145 129.888Z"
                        fill="#2684FF"
                    />
                    <path
                        d="M107.512 50.7847L81.3146 129.888L55.0235 50.7847H107.512Z"
                        fill="#4C9AFF"
                    />
                    <path
                        d="M160 50.7847L81.3146 129.888L107.512 50.7847H160Z"
                        fill="#B2D4FF"
                    />
                    <path
                        d="M28.8263 17.0854L55.0235 50.7846H2.53522L28.8263 17.0854Z"
                        fill="#4C9AFF"
                    />
                    <path
                        d="M81.3146 17.0854L107.512 50.7846H55.0235L81.3146 17.0854Z"
                        fill="#DEEBFF"
                    />
                    <path
                        d="M107.512 50.7846L81.3146 17.0854H133.709L107.512 50.7846Z"
                        fill="#B2D4FF"
                    />
                    <path
                        d="M55.0235 50.7846L28.8263 17.0854H81.3146L55.0235 50.7846Z"
                        fill="#B2D4FF"
                    />
                    <path
                        d="M133.709 17.0854L160 50.7846H107.512L133.709 17.0854Z"
                        fill="#DEEBFF"
                    />
                    <path
                        d="M28.6385 32.7552C29.5101 28.9352 31.4344 25.4391 34.1912 22.6678C36.9479 19.8964 40.4254 17.9618 44.2253 17.0856C40.4206 16.2198 36.9375 14.2886 34.1791 11.5156C31.4207 8.74252 29.4997 5.24094 28.6385 1.41602C27.809 5.25565 25.8985 8.77442 23.1348 11.5527C20.3712 14.3311 16.871 16.2517 13.0516 17.0856C16.8661 17.9303 20.3605 19.8545 23.1224 22.6311C25.8842 25.4076 27.7983 28.9206 28.6385 32.7552Z"
                        fill="#0065FF"
                    />
                    <path
                        d="M28.8263 121.77C27.9704 117.933 26.053 114.417 23.2952 111.627C20.5373 108.838 17.0514 106.889 13.2394 106.006C17.0539 105.161 20.5483 103.237 23.3102 100.461C26.072 97.6841 27.9861 94.1711 28.8263 90.3364C29.6558 94.1761 31.5663 97.6948 34.33 100.473C37.0936 103.251 40.5938 105.172 44.4132 106.006C40.5963 106.879 37.1048 108.824 34.3453 111.615C31.5857 114.406 29.6718 117.928 28.8263 121.77Z"
                        fill="#0065FF"
                    />
                    <path
                        d="M4.50704 32.283C4.73185 31.1813 5.26763 30.1681 6.05029 29.3645C6.83294 28.561 7.82931 28.0011 8.92019 27.752C7.83662 27.5207 6.84302 26.9785 6.05974 26.1911C5.27646 25.4036 4.73712 24.4047 4.50704 23.3154C4.23859 24.4022 3.67497 25.3926 2.87911 26.176C2.08324 26.9594 1.08644 27.5051 0 27.752H0C1.09361 28.0164 2.09327 28.5795 2.88861 29.379C3.68396 30.1786 4.24405 31.1836 4.50704 32.283Z"
                        fill="#0065FF"
                    />
                    <path
                        d="M50.6103 126.206C50.3625 125.11 49.8056 124.108 49.0063 123.321C48.207 122.534 47.1991 121.996 46.1033 121.77C47.1969 121.505 48.1965 120.942 48.9919 120.143C49.7872 119.343 50.3473 118.338 50.6103 117.239C50.8351 118.34 51.3709 119.354 52.1536 120.157C52.9362 120.961 53.9326 121.521 55.0235 121.77C53.9348 121.99 52.9351 122.528 52.15 123.318C51.3648 124.107 50.8291 125.112 50.6103 126.206Z"
                        fill="#0065FF"
                    />
                    <path
                        d="M113.146 17.0856V11.1387C113.146 9.86192 113.396 8.59762 113.882 7.41801C114.368 6.23839 115.08 5.16657 115.978 4.26373C116.876 3.36089 117.942 2.64473 119.116 2.15611C120.289 1.6675 121.547 1.41602 122.817 1.41602V1.41602C125.391 1.4159 127.86 2.43723 129.689 4.25793C131.518 6.07863 132.557 8.55146 132.582 11.1387V91.7523"
                        stroke="#FFC400"
                        strokeWidth="3"
                        strokeMiterlimit="10"
                    />
                    <path
                        d="M132.582 95.8113C133.827 95.8113 134.836 94.797 134.836 93.5458C134.836 92.2946 133.827 91.2803 132.582 91.2803C131.338 91.2803 130.329 92.2946 130.329 93.5458C130.329 94.797 131.338 95.8113 132.582 95.8113Z"
                        fill="white"
                    />
                    <path
                        d="M130.516 147.729V143.764C127.857 143.613 125.247 142.974 122.817 141.876V135.835C126.017 137.386 129.502 138.254 133.052 138.384C135.775 138.384 136.808 137.345 136.808 135.646C136.808 133.947 135.775 133.381 132.394 132.626C125.164 131.21 122.629 128.472 122.629 124.319C122.629 120.165 125.352 117.428 130.516 116.673V112.614H135.587V116.673C137.916 116.876 140.198 117.449 142.347 118.372V124.224C139.405 122.818 136.213 122.016 132.958 121.865C130.516 121.865 129.108 122.714 129.108 124.413C129.108 126.112 130.423 126.584 133.991 127.434C141.127 128.755 143.474 131.21 143.474 135.552C143.474 139.894 141.127 143.009 135.587 143.67V147.729H130.516Z"
                        fill="white"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_237_1573">
                        <rect width="160" height="160" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        </div>
    );
};
